import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid } from '@mui/material';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import useMediaQuery from '@mui/material/useMediaQuery';

import AccessController from "../../components/access_controller";
import UserProfile from "../../components/User";
import { VisuallyHiddenInput, getErrorMessage } from "../../utils/utils";
import api from "../../utils/webcall";


// Platforms the backend has a report parser for. The model accepts more, but
// uploading one of those returns an explanatory 400 rather than mis-mapping the
// columns, so only the supported ones are offered here.
const SUPPORTED_PLATFORMS = ['Zomato', 'Swiggy', 'Ownly Food'];
const PENDING_PLATFORMS = ['Olafood', 'UberEats'];

// Ownly Food exports an .xlsx workbook; the other two are CSV.
const PLATFORM_FILE_TYPES = {
    'Zomato': '.csv',
    'Swiggy': '.csv',
    'Ownly Food': '.xlsx,.xls',
};

function OrderUploadForm(props) {
    const { showErrorMessage, showSuccessMessage, onUploaded } = props;
    const isMobile = useMediaQuery('(max-width:600px)');
    const [selectedFiles, setSelectedFiles] = useState(null);
    const [platform, setPlatform] = useState('Zomato');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const handleSubmit = () => {
        setSubmitting(true);
        setResult(null);

        const form_data = new FormData();
        // The API requires the platform so it can pick the right report parser.
        form_data.append('platform', platform);
        Array.from(selectedFiles).forEach((file) => {
            form_data.append(file.name, file);
        });

        api.post(`/pos/orders/upload/`, form_data).then((res) => {
            const data = res.data || {};
            setResult(data);
            showSuccessMessage(
                `Ingested ${data.created || 0} new and updated ${data.updated || 0} existing orders.`
            );
            if (onUploaded) onUploaded();
        }).catch((error) => {
            showErrorMessage(getErrorMessage(error));
        }).finally(() => {
            setSubmitting(false);
        });
    }

    return (
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: isMobile ? '90%' : 460,
                maxWidth: 460,
                maxHeight: '85vh',
                overflowY: 'auto',
                bgcolor: 'background.paper',
                borderRadius: 1,
                boxShadow: 24,
                p: isMobile ? 2 : 3,
            }}
        >
            <Grid
                container
                direction={'column'}
                rowSpacing={2}
            >
                <Grid>
                    <Typography id="modal-modal-title" variant="h6" component="h2">
                        Upload order report
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Export the order-level report from the platform partner dashboard,
                        then upload the CSV here. Re-uploading an overlapping date range is
                        safe: existing orders are updated, not duplicated.
                    </Typography>
                </Grid>

                <Grid>
                    <FormControl fullWidth size="small">
                        <InputLabel id="platform-select-label">Platform</InputLabel>
                        <Select
                            labelId="platform-select-label"
                            label="Platform"
                            value={platform}
                            onChange={(e) => setPlatform(e.target.value)}
                        >
                            {SUPPORTED_PLATFORMS.map((name) => (
                                <MenuItem key={name} value={name}>{name}</MenuItem>
                            ))}
                            {PENDING_PLATFORMS.map((name) => (
                                <MenuItem key={name} value={name} disabled>
                                    {name} (parser not written yet)
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid>
                    <Button
                        component="label"
                        role={undefined}
                        variant="outlined"
                        tabIndex={-1}
                        fullWidth
                    >
                        {platform === 'Ownly Food' ? 'Choose Excel file(s)' : 'Choose CSV file(s)'}
                        <VisuallyHiddenInput
                            type="file"
                            accept={PLATFORM_FILE_TYPES[platform] || '.csv'}
                            onChange={(event) => {
                                setSelectedFiles(event.target.files);
                                setResult(null);
                            }}
                            multiple
                        />
                    </Button>
                </Grid>

                {selectedFiles !== null && (
                    <Grid>
                        {Array.from(selectedFiles).map((item, index) => (
                            <Alert severity="info" sx={{ mb: 0.5 }} key={`file-${index}`}>
                                {item.name}
                            </Alert>
                        ))}
                    </Grid>
                )}

                <Grid>
                    <Button
                        variant="contained"
                        fullWidth
                        disabled={selectedFiles === null || submitting}
                        onClick={handleSubmit}
                    >
                        {submitting ? 'Uploading...' : 'Submit'}
                    </Button>
                </Grid>

                {result && (
                    <Grid>
                        <Alert severity={result.errors && result.errors.length ? 'warning' : 'success'}>
                            {result.created} created, {result.updated} updated.
                            {result.errors && result.errors.length > 0 && (
                                <Box component="ul" sx={{ pl: 2, mt: 1, mb: 0 }}>
                                    {result.errors.slice(0, 5).map((err, index) => (
                                        <li key={`err-${index}`}>
                                            <Typography variant="caption">{err}</Typography>
                                        </li>
                                    ))}
                                    {result.errors.length > 5 && (
                                        <li>
                                            <Typography variant="caption">
                                                ...and {result.errors.length - 5} more skipped row(s).
                                            </Typography>
                                        </li>
                                    )}
                                </Box>
                            )}
                        </Alert>
                    </Grid>
                )}
            </Grid>
        </Box>
    )
}

function ViewOrders(props) {
    const { showErrorMessage, showSuccessMessage } = props;
    const username = UserProfile.getName();
    const [showOrderUploadModel, setOrderUploadModel] = useState(false);
    const navigate = useNavigate();

    return (
        <AccessController>
            <Modal
                open={showOrderUploadModel}
                onClose={(e) => {
                    setOrderUploadModel(false)
                }}
            >
                <>
                    <OrderUploadForm
                        showErrorMessage={showErrorMessage}
                        showSuccessMessage={showSuccessMessage}
                    />
                </>
            </Modal>
            <Grid
                container
                direction={'column'}
                rowSpacing={2}
                alignItems="center"
                sx={{ px: 2 }}
            >
                <h2>DIVASH | ADMIN</h2>
                <Grid>
                    <i>Welcome {username}</i>
                </Grid>
                <Grid sx={{ textAlign: 'center', maxWidth: 520 }}>
                    <Typography variant="body2" color="text.secondary">
                        Upload platform order reports here. Once ingested, revenue, platform
                        mix, discounts, ratings and margin are on the performance dashboard.
                    </Typography>
                </Grid>
                <Grid>
                    <Button
                        variant="contained"
                        sx={{ minWidth: '160px', m: 0.5 }}
                        onClick={(e) => {
                            setOrderUploadModel(true)
                        }}
                    >
                        Add Orders
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{ minWidth: '160px', m: 0.5 }}
                        onClick={() => navigate('/analytics')}
                    >
                        View Performance
                    </Button>
                </Grid>
            </Grid>
        </AccessController>
    )
}

export default ViewOrders;